import * as React from 'react';

import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
import {ErrorState} from 'shells/error-state/error-state';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Body} from 'shells/typography/body';
import {UppercaseLabel} from 'shells/typography/uppercase-label';

import type {BillingInfo as BillingInfoType} from 'nutshell-graphql-types';

import {AddressInfo} from './address-info';
import {BillingForm} from './billing-form/billing-form';
import {ContactInfo} from './contact-info';
import {PaymentInfo} from './payment-info';

import styles from './billing-form/billing-form.module.css';

type Props = {
    billingInfo: BillingInfoType | null | undefined;
    refetchBillingInfo: () => Promise<any>;
    isLoading: boolean;
    errorMessage: string | null | undefined;
    isViewingBillingForm: boolean;
    setIsViewingBillingForm: (arg1: boolean) => void;
    setHasUnsavedBillingChanges?: (arg1: boolean) => void;
};

export function BillingInfo(props: Props) {
    const {
        billingInfo,
        refetchBillingInfo,
        isLoading,
        errorMessage,
        isViewingBillingForm,
        setIsViewingBillingForm,
        setHasUnsavedBillingChanges,
    } = props;

    return (
        <>
            {isViewingBillingForm ? (
                <BillingForm
                    billingInfo={billingInfo}
                    refetchBillingInfo={refetchBillingInfo}
                    closeForm={() => setIsViewingBillingForm(false)}
                    isLoadingBillingInfo={isLoading}
                    setHasUnsavedBillingChanges={setHasUnsavedBillingChanges}
                />
            ) : (
                <div>
                    <Card hasBorder={true} fitHeight={true}>
                        <div className='pad-16'>
                            <div className={styles.header}>
                                <UppercaseLabel>Billing information</UppercaseLabel>
                                <Button
                                    onClick={() => setIsViewingBillingForm(true)}
                                    variant='text-primary'
                                    noPadding={true}
                                >
                                    Edit
                                </Button>
                            </div>
                            {isLoading && <LoadingPage />}
                            {errorMessage && (
                                <ErrorState errorMessage={errorMessage} size='small' />
                            )}
                            {!isLoading && !errorMessage && !billingInfo && (
                                <Body color='light' size='small' isItalic={true}>
                                    No billing information found
                                </Body>
                            )}
                            {billingInfo && !isLoading && !errorMessage && (
                                <StackLegacy spacing={16}>
                                    <ContactInfo
                                        displayName={billingInfo.displayName}
                                        email={billingInfo.email}
                                        phoneNumber={billingInfo.phoneNumberFormatted}
                                    />
                                    <AddressInfo
                                        addressInfo={billingInfo.addressInfo}
                                        companyName={billingInfo.companyName}
                                    />
                                    <PaymentInfo
                                        cardInfo={billingInfo.cardInfo}
                                        bankInfo={billingInfo.bankInfo}
                                    />
                                </StackLegacy>
                            )}
                        </div>
                    </Card>
                </div>
            )}
        </>
    );
}
